import { NetworkStatus, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useNavigate, useParams, Routes, Route } from "react-router-dom";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import DocumentationIconButton from "components/DocumentationIconButton";
import NoAccessPage from "components/error/NoAccessPage";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import MetaInfoList from "components/MetaInfoList";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Box from "ds/components/Box";
import Tab from "ds/components/Tab";
import TabWithCounter from "ds/components/Tab/WithCounter";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { AuthorizationScheme, PolicyEngineType, PolicyType } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import LoginPolicyManageDropdown from "views/Account/LoginPolicyDetails/ManageDropdown";
import { AccountContext } from "views/AccountWrapper";

import { showLoginPolicyEditDetailsDrawer } from "../LoginPolicyEditDetailsDrawer";
import useTakenPolicyNames from "../Policies/useTakenPolicyNames";
import PolicySimulation from "../PolicySimulation";
import { ManagementStrategy } from "../Settings/types";
import LoginPolicyDetailsBody from "./Body";
import { GET_POLICY } from "./gql";
import LoginPolicyDetailsSamples from "./Samples";
import styles from "./styles.module.css";
import { PolicyQueryResponse } from "./types";

const LoginPolicyDetails = () => {
  const { accountName, viewer } = useTypedContext(AccountContext);

  useTitle(`Login policy details · ${accountName}`);

  const params = useParams<{ policyId?: string }>();
  const navigate = useNavigate();

  const { policyId } = params;

  const [body, setBody] = useState("");
  const handleEditorChange = (value?: string) => {
    setBody(value || "");
  };

  const [isSimulationVisible, setIsSimulationVisible] = useState(false);

  const deleteCallback = () => navigate(`/settings/login-policy`);

  const {
    loading: isPolicyLoading,
    data,
    networkStatus,
    error,
  } = useQuery<PolicyQueryResponse>(GET_POLICY, {
    variables: {
      policyId,
    },
    skip: !policyId,
  });

  const [policyEngineType, setPolicyEngineType] = useState<PolicyEngineType>(
    PolicyEngineType.RegoV1
  );

  useEffect(() => {
    if (data?.policy) {
      setBody(data.policy.body);
      setPolicyEngineType(data.policy.engineType);
    }
  }, [data?.policy]);

  useBreadcrumbs(
    [
      {
        title: "Organization settings",
      },
      {
        title: "Login Policy",
        link: "/settings/login-policy",
      },
      {
        title: data?.policy?.name || "",
      },
    ],
    [data?.policy?.name]
  );
  const takenPolicyNames = useTakenPolicyNames();

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPolicyLoading && networkStatus !== NetworkStatus.refetch && !data?.policy) {
    return <PageLoading />;
  }

  if (!viewer.admin) {
    return <NoAccessPage />;
  }

  if (!policyId || !data?.policy) {
    return <NotFoundPage />;
  }

  const isBodyReadyToUpdate = data.policy.body !== body;
  const isLoginPolicyActive = data?.authorizationScheme !== AuthorizationScheme.ManagedUsers;

  const policyUrl = `/login-policy/${data.policy.id}`;
  const policySamplesUrl = `${policyUrl}/samples`;

  return (
    <>
      <ViewHeader
        backConfirmation={
          isBodyReadyToUpdate ? "Your changes in policy body will not be saved." : undefined
        }
      >
        <ViewHeaderWrapper direction="row" align="center" justify="between" fullWidth>
          <ViewHeaderTitle>{data.policy.name}</ViewHeaderTitle>
          <Box gap="large">
            <DocumentationIconButton
              href={getDocsUrl("/concepts/policy/login-policy.html")}
              tooltipText="Go to login policies documentation"
            />
            <LoginPolicyManageDropdown
              id={data.policy.id}
              name={data.policy.name}
              handleLoginPolicyEditDetailsClick={() =>
                showLoginPolicyEditDetailsDrawer({
                  policyId: data.policy.id,
                  name: data.policy.name,
                  labels: data.policy.labels,
                  body: data.policy.body,
                  takenPolicyNames,
                })
              }
              loginPolicyDeleteCallback={deleteCallback}
              buttonVariant="secondary"
              size="medium"
              isLoginPolicyActive={isLoginPolicyActive}
              analyticsPage={AnalyticsPageOrganization.OrganizationLoginPolicyDetails}
            />
          </Box>
        </ViewHeaderWrapper>

        <ViewHeaderScrollCollapse>
          <MetaInfoList>
            <TimestampMetaInfo prefix="Updated on" timestamp={data.policy.updatedAt} />
          </MetaInfoList>
        </ViewHeaderScrollCollapse>

        <ViewHeaderNavigation>
          <Tab end to={policyUrl} label="Policy body" />
          <TabWithCounter count={data.policy.sampleCount} to={policySamplesUrl} label="Samples" />
        </ViewHeaderNavigation>
      </ViewHeader>

      <Routes>
        <Route
          index
          element={
            <Box justify="stretch" className={styles.pageContent}>
              <LoginPolicyDetailsBody
                body={body}
                handleEditorChange={handleEditorChange}
                policyEngineType={policyEngineType}
                setPolicyEngineType={setPolicyEngineType}
                policy={data.policy}
                isSimulationVisible={isSimulationVisible}
                isLoginPolicyActive={isLoginPolicyActive}
              />
              <PolicySimulation
                className={styles.simulationWrapper}
                policyId={data.policy.id}
                body={body}
                type={PolicyType.Login}
                engineType={policyEngineType}
                isSimulationVisible={isSimulationVisible}
                setIsSimulationVisible={setIsSimulationVisible}
                defaultAnalyticsProperties={{
                  managementStrategy: isLoginPolicyActive
                    ? ManagementStrategy.LOGIN_POLICY
                    : ManagementStrategy.USER_MANAGEMENT,
                }}
              />
            </Box>
          }
        />
        <Route path="samples" element={<LoginPolicyDetailsSamples id={data.policy.id} />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
};

export default LoginPolicyDetails;
